import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PaginationBar } from '../../components/ReportTableKit';
import { VoucherSummaryCards } from '../../components/vouchers/VoucherSummaryCards';
import { VoucherFilters } from '../../components/vouchers/VoucherFilters';
import { VoucherTable } from '../../components/vouchers/VoucherTable';
import { CampaignCourses } from '../../components/vouchers/CampaignCourses';
import { VoucherLearnerDrawer } from '../../components/vouchers/VoucherLearnerDrawer';

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/vouchers/issuance`;

function useDebounced(value, ms = 400) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export default function VouchersPage() {
  const [options, setOptions] = useState({});
  const [filters, setFilters] = useState(null);
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState({ data: [], total: 0, total_pages: 1, pii_allowed: false });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [learner, setLearner] = useState(null);
  const q = useDebounced(filters);

  useEffect(() => {
    axios.get(`${BASE}/filters`, { withCredentials: true })
      .then(r => { setOptions(r.data); setFilters({ campaign: r.data.campaigns?.[0] || '' }); })
      .catch(e => { setError(e.response?.status === 403 ? 'This page is restricted to Technofocus users.' : 'Could not load voucher filters.'); setLoading(false); });
  }, []);

  useEffect(() => {
    if (filters === null) return;
    setSummary(null);
    axios.get(`${BASE}/summary`, { params: { campaign: filters.campaign || undefined }, withCredentials: true })
      .then(r => setSummary(r.data)).catch(() => setSummary({}));
  }, [filters?.campaign]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!q) return;
    let live = true;
    setLoading(true); setError(null);
    axios.get(`${BASE}/list`, { params: { ...q, page, per_page: perPage }, withCredentials: true })
      .then(r => { if (live) setData(r.data); })
      .catch(() => { if (live) setError('Failed to load vouchers. Please try again.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [q, page, perPage]);

  const onChange = (k, v) => { setPage(1); setFilters(f => ({ ...f, [k]: v })); };
  const onClear = () => { setPage(1); setFilters({ campaign: filters?.campaign || '' }); };

  async function exportCsv() {
    setExporting(true);
    try {
      const r = await axios.get(`${BASE}/export`, { params: filters, withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement('a'), { href: url, download: `vouchers_${(filters.campaign || 'all').replace(/\s+/g, '_')}.csv` });
      a.click(); URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed. Please try again.');
    } finally { setExporting(false); }
  }

  return (
    <div data-testid="vouchers-page">
      <div className="page-header"><h1 data-testid="vouchers-title">Vouchers</h1></div>
      <VoucherSummaryCards summary={summary} loading={!summary} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>Manage and track all disbursed vouchers</p>
        <button className="btn btn-secondary" onClick={exportCsv} disabled={exporting || !filters} data-testid="voucher-export-btn"
          title="Download CSV" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Export CSV
        </button>
      </div>
      {filters && (
        <>
          <VoucherFilters options={options} filters={filters} onChange={onChange} onClear={onClear}
            perPage={perPage} onPerPage={n => { setPage(1); setPerPage(n); }} piiAllowed={data.pii_allowed} />
          <CampaignCourses campaign={filters.campaign} />
        </>
      )}
      <VoucherTable rows={data.data || []} loading={loading} error={error} onLearner={setLearner} />
      {learner && <VoucherLearnerDrawer lmsId={learner} onClose={() => setLearner(null)} />}
      {!loading && !error && data.total > 0 && (
        <PaginationBar page={data.page} totalPages={data.total_pages} total={data.total} perPage={perPage} onPage={setPage} testId="voucher-pagination" />
      )}
    </div>
  );
}
